// Chat messages of phone v2 (F_Chat modes stuck / help, F_Phone), shared with the desktop chat:
//   <StuckMsg/>   kind 'stuck': her line + the reasons card (step 'ask': "STUCK ON: …", six reasons, "Or just tell me,
//                 in your own words.") or the help card (step 'help': FIRST STEP · 2 MIN / TWO MINUTES / TOGETHER /
//                 ROUGH VERSION · 2 MIN + Start, WHEN IS BETTER? chips, "too big" split cards + Use these, then
//                 "Something else"). Once acted on, the help collapses to her line + the outcome line.
//   <DoneNote/>   kind 'done': the tick line ("You ticked off “…” · +15 points") + her done line. Hidden after Undo.
//   <WorkMsg/>    kind 'work' (11-05): her "Done, or keep going?" while working together; while it waits a card with
//                 Done / Keep going and Stuck; ended with no answer: "“…” is back on today's list".
//   <StartMsg/>   kind 'start' (11-05): her Start together for a card, under her line; once taken "Working on it with …".
//   <ChatPill/>   the centred glass line they use.
import type { ComponentChildren } from 'preact';
import { useState } from 'preact/hooks';
import type { Card, CharId, DoneNoteData, Message, StartOfferData, StuckAction, StuckChatData, StuckReason, WorkCheckData } from '@shared/types';
import { STUCK_REASONS } from '@shared/content/lines';
import { STUCK_CARD, stuckAlt, stuckStep } from '@shared/content/stuck';
import { START_BTN, WORK_BTN, startedNote, workEndedNote } from '@shared/content/work';
import { taskShort } from '@shared/rules';
import { stuckAction, stuckChat } from '../../store';
import { go } from '../../router';
import { Bubble, Icon, MessageRow, PrimaryButton, type IconName } from '../../components';
import s from './chatCards.module.css';

export function ChatPill({ icon, children }: { icon: IconName; children: ComponentChildren }) {
  return (
    <p class={s.pill}>
      <Icon name={icon} size={16} strokeWidth={2.6} color="var(--acc)" />
      <span>{children}</span>
    </p>
  );
}

/** The 'done' note. Returns null once undone (the app hides it). */
export function DoneNote({ m, ch }: { m: Message; ch: CharId }) {
  const d = m.data as DoneNoteData | undefined;
  if (!d || d.undone) return null;
  return (
    <div class={s.stack}>
      <ChatPill icon="check">{m.text}</ChatPill>
      {d.line && <Bubble from="buddy" ch={ch} face={false} e={2}>{d.line}</Bubble>}
    </div>
  );
}

/** Her ask is live: still waiting, and the card is still being worked on in the session she asked about. */
export const workLive = (d: WorkCheckData | undefined, card: Card | null) => !!d && d.status === 'pending' && !!card && card.st === 'todo' && card.startedAt === d.at;

/** kind 'work': her line; while her ask is live a card with Done / Keep going and Stuck. */
export function WorkMsg({ m, ch, face, card, onDone, onKeep, onStuck }: {
  m: Message; ch: CharId; face: boolean; card: Card | null;
  onDone: (card: Card) => Promise<unknown>; onKeep: (messageId: string) => Promise<unknown>; onStuck: (card: Card) => void;
}) {
  const d = m.data as WorkCheckData | undefined;
  const [busy, setBusy] = useState<string | null>(null);
  if (!d) return null;
  const live = workLive(d, card);
  const run = async (k: string, fn: () => Promise<unknown>) => { if (busy) return; setBusy(k); try { await fn(); } finally { setBusy(null); } };
  return (
    <div class={s.stack}>
      <Bubble from="buddy" ch={ch} face={face} e={3}>{m.text}</Bubble>
      {live && card && (
        <div class={s.card} data-help="true" role="group" aria-label={`Working on: ${d.title}`}>
          <p class={s.head}>{`WORKING TOGETHER · ${d.min} MIN PLANNED`}</p>
          <p class={s.title}>{d.title}</p>
          <div class={s.btns}>
            <PrimaryButton h={48} icon="check" grow={1.2} busy={busy === 'done'} onClick={() => void run('done', () => onDone(card))} data-cta="work-done">{WORK_BTN.done}</PrimaryButton>
            <button type="button" class={s.chip} style={{ flex: '1 1 0' }} aria-busy={busy === 'keep' ? 'true' : undefined} onClick={() => void run('keep', () => onKeep(m.id))}>{WORK_BTN.keep}</button>
          </div>
          <button type="button" class={s.other} onClick={() => onStuck(card)}>{WORK_BTN.stuck}</button>
        </div>
      )}
      {d.status === 'ended' && <ChatPill icon="arrow">{workEndedNote(d.title)}</ChatPill>}
    </div>
  );
}

/** kind 'start': her Start together for a card still to do; once taken, the note. Gone (done, moved, a newer offer): nothing. */
export function StartMsg({ m, ch, card, onStart }: { m: Message; ch: CharId; card: Card | null; onStart: (card: Card) => void }) {
  const d = m.data as StartOfferData | undefined;
  if (!d) return null;
  if (d.status === 'started') return <ChatPill icon="play">{startedNote(ch, d.title)}</ChatPill>;
  if (d.status !== 'pending' || !card || card.st !== 'todo' || card.startedAt) return null;
  return (
    <div class={s.card} role="group" aria-label={`Start together: ${card.title}`}>
      <MessageRow time={card.time} title={card.title} side={`${card.min} min`} />
      <PrimaryButton h={48} icon="play" onClick={() => onStart(card)} data-cta="start-together">{START_BTN}</PrimaryButton>
    </div>
  );
}

/**
 * A stuck message. `current`: for an ask, whether its reasons card still shows (it is open, or its help is the open
 * one, so the picked reason stays lit above it). `onWait` is told while a reason is on its way (her help may take a
 * few seconds), so the chat can show her typing.
 */
export function StuckMsg({ m, ch, face, current, onWait }: {
  m: Message; ch: CharId; face: boolean; current: boolean; onWait?: (waiting: boolean) => void;
}) {
  const d = m.data as StuckChatData | undefined;
  const [busy, setBusy] = useState<string | null>(null);
  if (!d) return m.text ? <Bubble from="buddy" ch={ch} face={face} e={4}>{m.text}</Bubble> : null;
  const line = m.text ? <Bubble from="buddy" ch={ch} face={face} e={d.step === 'ask' ? 4 : 1}>{m.text}</Bubble> : null;
  const open = d.status === 'open';

  const act = async (key: string, a: StuckAction) => {
    if (busy) return;
    setBusy(key);
    const r = await stuckAction(m.id, a);
    setBusy(null);
    if (r && a.action === 'start' && d.help) go('W', { cur: r.cardId || d.cardId, alt: stuckAlt(d.help, d.title), from: 'C' });
  };

  if (d.step === 'ask') {
    if (!open && !current) return line;
    const pick = async (reason: StuckReason) => {
      if (!open || busy) return;
      setBusy(reason); onWait?.(true);
      await stuckChat(d.cardId, reason);
      setBusy(null); onWait?.(false);
    };
    return (
      <div class={s.stack}>
        {line}
        <div class={s.card} role="group" aria-label={`Stuck on: ${d.title}`}>
          <p class={s.head}>{`STUCK ON: ${taskShort(d.title).toUpperCase()}`}</p>
          <div class={s.grid2}>
            {STUCK_REASONS.map((r) => {
              const on = d.reason === r.id || busy === r.id;
              return (
                <button
                  key={r.id} type="button" class={s.reason} aria-pressed={on ? 'true' : 'false'}
                  data-dim={(d.reason || busy) && !on ? 'true' : 'false'} aria-disabled={!open || !!busy ? 'true' : undefined}
                  onClick={() => void pick(r.id)}
                >{r.label}</button>
              );
            })}
          </div>
          <p class={s.own}>Or just tell me, in your own words.</p>
        </div>
      </div>
    );
  }

  // step 'help'
  const h = d.help;
  if (!open || !h) {
    return (
      <div class={s.stack}>
        {line}
        {d.status === 'resolved' && d.out && <ChatPill icon="arrow">{d.out}</ChatPill>}
      </div>
    );
  }
  const split = h.reason === 'big' && h.split && h.split.length >= 2 ? h.split : null;
  const step = split ? null : stuckStep(h, d.title);
  return (
    <div class={s.stack}>
      {line}
      <div class={s.card} data-help="true" role="group" aria-label={`Help with: ${d.title}`}>
        <p class={s.head}>{split ? `SPLIT INTO ${split.length} SMALL CARDS` : STUCK_CARD[h.reason].head}</p>
        {step && <p class={s.title}>{step}</p>}
        {split && <div class={s.rows}>{split.map((x, i) => <MessageRow key={i} time={x.time} title={x.title} side={`${x.min} min`} />)}</div>}
        {h.reason === 'time' ? (
          <div class={s.grid2}>
            {h.chips.map((t) => (
              <button key={t} type="button" class={s.chip} aria-busy={busy === t ? 'true' : undefined} onClick={() => void act(t, { action: 'later', when: t })}>{t}</button>
            ))}
          </div>
        ) : split ? (
          <PrimaryButton h={48} busy={busy === 'split'} onClick={() => void act('split', { action: 'split' })}>{`Use these ${split.length} cards`}</PrimaryButton>
        ) : (
          <PrimaryButton h={48} icon="play" busy={busy === 'start'} onClick={() => void act('start', { action: 'start' })}>{STUCK_CARD[h.reason].btn}</PrimaryButton>
        )}
        <button type="button" class={s.other} onClick={() => void act('again', { action: 'again' })}>Something else</button>
      </div>
    </div>
  );
}
